import duckify from '../../tools/duckify';

export const namespace = 'cassie';
export const store = 'csqueeze';

export const Types = duckify(namespace, store, [
  'STUDY_DATA',
  'TRANSFORM_CLASS_TO_POLYGONS',
  'ACQUIRE_IMAGES_MEDIAN',
  'CLASS_LAND_COVERS',
  'CALCULE_BASE_LINE',
  'WATER_LAND_COVER',
  'TRANSECTS_COORDS',
  'TRANSECTS_RESULTS',
  'RESET_CLASS_LAND_COVERS',
  'RESET_WATER_LAND_COVER',
  'SET_YEARS',
  'SET_DATES',
  'SET_MISSIONS',
  'SET_PERIOD_LABELS',
  'ADD_IMAGE_LIST',
  'SET_IMAGE_LIST_AT',
  'TRUNCATE_IMAGE_LIST',
  'REMOVE_LAST_IMAGE_LIST',
  'RESET_IMAGE_LIST',
  'RESET',
]);

export const Actions = {
  /**
   * @function acquireImagesMedian
   * @description Builds the action requesting median-composite image acquisition for the given years.
   * @param {Array} years - Selected years and their image info.
   * @param {Number} elevation - Elevation threshold used to mask the acquired images.
   * @param {...*} extras - Extra arguments forwarded in the payload.
   * **Called by:
   * @see {LandCover}
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type ACQUIRE_IMAGES_MEDIAN.
   */
  acquireImagesMedian: (years, elevation, ...extras) => ({
    type: Types.ACQUIRE_IMAGES_MEDIAN,
    payload: { years, elevation, extras },
  }),

  /**
   * @function setYears
   * @description Builds the action storing the years selected for acquisition.
   * @param {Array} years - Selected years.
   * **Called by:
   * @see {SelectDatesCSqueeze}
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type SET_YEARS.
   */
  setYears: (years) => {
    return {
      type: Types.SET_YEARS,
      payload: { years },
    };
  },

  /**
   * @function setDates
   * @description Builds the action storing the dates chosen for each selected year.
   * @param {Array} dates - Chosen dates.
   * **Called by:
   * @see {SelectDatesCSqueeze}
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type SET_DATES.
   */
  setDates: (dates) => {
    return {
      type: Types.SET_DATES,
      payload: { dates },
    };
  },

  /**
   * @function setMissions
   * @description Builds the action storing the missions available for the selected years/dates.
   * @param {Array} missions - Available missions.
   * **Called by:
   * @see {SelectDatesCSqueeze}
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type SET_MISSIONS.
   */
  setMissions: (missions) => {
    return {
      type: Types.SET_MISSIONS,
      payload: { missions },
    };
  },

  /**
   * @function setPeriodLabels
   * @description Builds the action storing what each acquisition period is displayed as. The
   * period key itself stays the year (plus a `-1`/`-2` suffix when a year was picked twice) because
   * the layers, the land-cover vectors and the transect measurements are all stored against it and
   * the MapBiomas year is read off it; the label is what a person reads, and for the years that
   * repeat it is the period's own `DD/MM/YY - DD/MM/YY` date range instead of that suffix.
   * @param {Object} periodLabels - `{periodKey: label}` map.
   * **Called by:
   * @see {handleConfirmElevation} - CSqueeze, when the classification step's tabs are built.
   * @see {importSessionState} - to rebuild the labels along with the layers.
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type SET_PERIOD_LABELS.
   */
  setPeriodLabels: (periodLabels) => {
    return {
      type: Types.SET_PERIOD_LABELS,
      payload: { periodLabels },
    };
  },

  /**
   * @function setStudyData
   * @description Builds the action storing the study's transect/baseline configuration.
   * @param {Object} studyData - Study configuration data.
   * **Called by:
   * @see {LandCover}
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type STUDY_DATA.
   */
  setStudyData: (studyData) => {
    return {
      type: Types.STUDY_DATA,
      payload: { studyData },
    };
  },

  /**
   * @function transformClassToPolygons
   * @description Builds the action requesting a classified image to be vectorized into land-cover polygons.
   * @param {ee.Image} classification - Classified image.
   * @param {ee.Projection} imageProjection - Classification image's projection.
   * @param {Number} year - Year the classification belongs to.
   * @param {String} type - Classification type (e.g. "mapbiomas").
   * @param {Array} AOICoords - Area of interest polygon coordinates.
   * **Called by:
   * @see {ProcessingPage} - CSqueeze module's processing page (imported as ProcessingCSqueezePage).
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type TRANSFORM_CLASS_TO_POLYGONS.
   */
  transformClassToPolygons: (classification, imageProjection, year, type, AOICoords) => ({
    type: Types.TRANSFORM_CLASS_TO_POLYGONS,
    payload: { classification, imageProjection, year, type, AOICoords },
  }),

  /**
   * @function setClassLandCovers
   * @description Builds the action storing a year's vectorized (non-water) land-cover polygons.
   * @param {Number} year - Year the land covers belong to.
   * @param {Array} classLandCovers - Vectorized land-cover geometries.
   * **Called by:
   * @see {handleTransformClassToPolygons}
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type CLASS_LAND_COVERS.
   */
  setClassLandCovers: (year, classLandCovers) => {
    return {
      type: Types.CLASS_LAND_COVERS,
      payload: { year, classLandCovers },
    };
  },

  /**
   * @function resetClassLandCovers
   * @description Builds the action clearing every cached land-cover polygon. Not currently
   * dispatched from any component.
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type RESET_CLASS_LAND_COVERS.
   */
  resetClassLandCovers: () => {
    return {
      type: Types.RESET_CLASS_LAND_COVERS,
    };
  },

  /**
   * @function setWaterLandCover
   * @description Builds the action appending a year's vectorized water land-cover geometry.
   * @param {Object} waterLandCover - {year, geometry} water land-cover descriptor.
   * **Called by:
   * @see {handleTransformClassToPolygons}
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type WATER_LAND_COVER.
   */
  setWaterLandCover: (waterLandCover) => {
    return {
      type: Types.WATER_LAND_COVER,
      payload: { waterLandCover },
    };
  },

  /**
   * @function resetWaterLandCover
   * @description Builds the action clearing every cached water land-cover geometry. Not
   * currently dispatched from any component.
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type RESET_WATER_LAND_COVER.
   */
  resetWaterLandCover: () => {
    return {
      type: Types.RESET_WATER_LAND_COVER,
    };
  },

  /**
   * @function setTransectsCoords
   * @description Builds the action storing the generated transects' coordinates. Not currently
   * dispatched from any component.
   * @param {Array} transectsCoords - Transect coordinates.
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type TRANSECTS_COORDS.
   */
  setTransectsCoords: (transectsCoords) => {
    return {
      type: Types.TRANSECTS_COORDS,
      payload: { transectsCoords },
    };
  },

  /**
   * @function setTransectsResults
   * @description Builds the action storing the computed per-transect land-cover results. Not
   * currently dispatched from any component (ProcessingPage tracks this in local state instead).
   * @param {Array} transectsResults - Computed transect results.
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type TRANSECTS_RESULTS.
   */
  setTransectsResults: (transectsResults) => {
    return {
      type: Types.TRANSECTS_RESULTS,
      payload: { transectsResults },
    };
  },

  /**
   * @function calculeBaseLine
   * @description Builds the action requesting the baseline/transects computation for the study.
   * Not currently dispatched from any component.
   * @param {Array} baseLineCoords - Baseline coordinates.
   * @param {Object} transectsData - Transect generation parameters.
   * @param {Array} years - Years included in the study.
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type CALCULE_BASE_LINE.
   */
  calculeBaseLine: (baseLineCoords, transectsData, years) => {
    return {
      type: Types.CALCULE_BASE_LINE,
      payload: { baseLineCoords, transectsData, years },
    };
  },

  setImageList: (imageList) => {
    return {
      type: Types.ADD_IMAGE_LIST,
      payload: { imageList },
    };
  },
  /**
   * @function setImageListAt
   * @description Writes one period's confirmed images into its own slot of `imageList`, replacing
   * whatever was there and dropping every slot after it. The two image steps of CSqueeze and
   * EUSCoast are the same component rendered twice, so before this each one only knew how to
   * append: confirming the same period twice (or simply walking the steps again) either left a
   * duplicate period behind or, through the `samePeriod` guard, silently dropped the second one.
   * Addressing the slot makes the write idempotent, and dropping the later slots is what keeps the
   * flow consistent when an earlier period changes - the periods after it were built on it.
   * @param {Number} index - The period slot, as the step's `periodIndex`.
   * @param {Array<Object>} imageList - The period's confirmed image infos.
   * **Called by:
   * @see {handleFinish} - CSqueeze/EUSCoast's PeriodImageChooser.
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type SET_IMAGE_LIST_AT.
   */
  setImageListAt: (index, imageList) => {
    return {
      type: Types.SET_IMAGE_LIST_AT,
      payload: { index, imageList },
    };
  },
  /**
   * @function truncateImageList
   * @description Drops every confirmed period from the given slot on. Stepping back out of a period
   * step abandons that period and everything after it, so the list has to shrink to just the slots
   * that survive - removing only the last one removed the wrong period when leaving the first image
   * step, which had already confirmed it.
   * @param {Number} index - The first period slot to drop.
   * **Called by:
   * @see {PeriodImageChooser} - CSqueeze/EUSCoast, on the back button.
   * @memberof module:Csqueeze
   * @returns {Object} Redux action of type TRUNCATE_IMAGE_LIST.
   */
  truncateImageList: (index) => {
    return {
      type: Types.TRUNCATE_IMAGE_LIST,
      payload: { index },
    };
  },
  removeLastImageList: () => {
    return {
      type: Types.REMOVE_LAST_IMAGE_LIST,
    };
  },
  /**
   * @function resetImageList
   * @description Builds the action emptying the confirmed image selections. `setImageList` appends
   * one period at a time, so a session import has to clear what is there before restoring the
   * file's own selections - otherwise importing twice in one tab leaves every period twice over.
   * **Called by:
   * @see {importSessionState} - CSqueeze's session import.
   * @memberof module:CSqueeze
   * @returns {Object} The action.
   */
  resetImageList: () => {
    return {
      type: Types.RESET_IMAGE_LIST,
    };
  },
  reset: () => {
    return {
      type: Types.RESET,
    };
  },
};

export default Actions;
